// Production Failover Service
// Switches between primary and secondary Firebase projects.
//
// CONTROL PLANE: the Firestore doc `system/failover` is the SINGLE source of
// truth for which project is active (field `activeProject`: 'primary' | 'secondary').
// Clients NEVER fail over autonomously — there is no client-side health check that
// flips projects. A client only switches when an admin/super-admin updates the
// control doc (see firestore.rules `match /system/{documentId}`), and only when a
// valid secondary project is configured. With no secondary config, the service
// runs in primary-only mode and does not even subscribe to the control doc.

import Logger from './logger';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore, doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getAuth, Auth } from 'firebase/auth';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import {
  FAILOVER_CONTROL_COLLECTION,
  FAILOVER_CONTROL_DOC_ID,
  FAILOVER_ACTIVE_PROJECT_FIELD,
  FAILOVER_PROJECT_SECONDARY,
} from '../constants/failoverControl';

// Environment Configuration Types
interface FirebaseProjectConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

interface FailoverState {
  isActive: boolean;
  currentProject: 'primary' | 'secondary';
  lastSwitchTime: Date | null;
  switchReason: string | null;
  dataSyncStatus: 'synced' | 'syncing' | 'out_of_sync';
  recoveryAttempts: number;
}

// Failover Service Class
class FailoverService {
  private primaryApp: FirebaseApp | null = null;
  private secondaryApp: FirebaseApp | null = null;
  private primaryDb: Firestore | null = null;
  private secondaryDb: Firestore | null = null;
  private primaryAuth: Auth | null = null;
  private secondaryAuth: Auth | null = null;
  private primaryStorage: FirebaseStorage | null = null;
  private secondaryStorage: FirebaseStorage | null = null;

  private failoverState: FailoverState = {
    isActive: false,
    currentProject: 'primary',
    lastSwitchTime: null,
    switchReason: null,
    dataSyncStatus: 'synced',
    recoveryAttempts: 0
  };

  private isInitialized = false;
  private controlDocUnsubscribe: Unsubscribe | null = null;

  constructor() {
    this.initializeFailoverService();
  }

  // Initialize the failover service
  private async initializeFailoverService(): Promise<void> {
    try {
      const primaryConfig = this.loadPrimaryConfig();
      const secondaryConfig = this.loadSecondaryConfig();

      if (!this.validateConfig(primaryConfig, 'primary')) {
        throw new Error('Primary Firebase configuration is invalid or missing');
      }

      if (!this.validateConfig(secondaryConfig, 'secondary')) {
        Logger.warn('[FailoverService] Secondary Firebase configuration is invalid or missing - running in primary-only mode');
      }

      await this.initializeFirebaseApps(primaryConfig, secondaryConfig);

      // Only watch the control plane when a real secondary exists to switch to.
      // With no secondary config there is nothing to fail over to, so we add no
      // listeners and remain strictly primary-only (zero behavioral change).
      if (this.secondaryDb) {
        this.subscribeToControlPlane();
      }

      this.isInitialized = true;
    } catch (error) {
      Logger.error('[FailoverService] Initialization failed:', error as Error);
      // Fall back to primary only - no disruption to existing system
      this.failoverState.currentProject = 'primary';
      this.failoverState.isActive = false;
    }
  }

  // Load primary project configuration from environment
  private loadPrimaryConfig(): FirebaseProjectConfig {
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ''
    };
  }

  // Load secondary project configuration from environment
  private loadSecondaryConfig(): FirebaseProjectConfig {
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_SECONDARY || '',
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_SECONDARY || '',
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_SECONDARY || '',
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_SECONDARY || '',
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_SECONDARY || '',
      appId: process.env.REACT_APP_FIREBASE_APP_ID_SECONDARY || '',
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_SECONDARY || ''
    };
  }

  // Validate Firebase configuration
  private validateConfig(config: FirebaseProjectConfig, projectName: string): boolean {
    const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];

    for (const field of requiredFields) {
      if (!config[field as keyof FirebaseProjectConfig] || config[field as keyof FirebaseProjectConfig] === '') {
        Logger.warn(`[FailoverService] Missing ${field} for ${projectName} project`);
        return false;
      }
    }

    return true;
  }

  // Initialize Firebase apps for both projects
  private async initializeFirebaseApps(
    primaryConfig: FirebaseProjectConfig,
    secondaryConfig: FirebaseProjectConfig
  ): Promise<void> {
    try {
      this.primaryApp = initializeApp(primaryConfig, 'primary');
      this.primaryDb = getFirestore(this.primaryApp);
      this.primaryAuth = getAuth(this.primaryApp);
      this.primaryStorage = getStorage(this.primaryApp);

      // Try to initialize secondary project, but don't fail if it's not configured
      try {
        if (this.validateConfig(secondaryConfig, 'secondary')) {
          this.secondaryApp = initializeApp(secondaryConfig, 'secondary');
          this.secondaryDb = getFirestore(this.secondaryApp);
          this.secondaryAuth = getAuth(this.secondaryApp);
          this.secondaryStorage = getStorage(this.secondaryApp);
        }
      } catch (secondaryError) {
        Logger.warn('[FailoverService] Secondary project initialization failed, running in primary-only mode:', secondaryError);
        this.secondaryApp = null;
        this.secondaryDb = null;
        this.secondaryAuth = null;
        this.secondaryStorage = null;
      }

    } catch (error) {
      Logger.error('[FailoverService] Primary Firebase app initialization failed:', error as Error);
      throw error;
    }
  }

  // Subscribe to the `system/failover` control doc on the PRIMARY project.
  // This is the only mechanism that can change the active project.
  private subscribeToControlPlane(): void {
    if (!this.primaryDb) return;
    try {
      const controlRef = doc(this.primaryDb, FAILOVER_CONTROL_COLLECTION, FAILOVER_CONTROL_DOC_ID);
      this.controlDocUnsubscribe = onSnapshot(
        controlRef,
        (snap) => {
          const desired = snap.exists()
            ? (snap.data()?.[FAILOVER_ACTIVE_PROJECT_FIELD] === FAILOVER_PROJECT_SECONDARY
                ? 'secondary'
                : 'primary')
            : 'primary';
          this.applyActiveProject(desired, 'system/failover control doc');
        },
        (error) => {
          // Permission denied (e.g. signed-out user) or transient stream error:
          // leave the active project unchanged. We must NOT yank a client that is
          // legitimately on secondary back to primary on a transient error, and we
          // must NOT auto-switch away from primary on error either. Only a
          // successful snapshot of the control doc ever changes the active project.
          Logger.warn('[FailoverService] Control-doc listener error (active project unchanged):', error);
        }
      );
    } catch (error) {
      Logger.warn('[FailoverService] Failed to subscribe to control plane; primary-only:', error);
    }
  }

  // Apply the desired active project. Switching to secondary requires a valid
  // secondary project; otherwise we remain on primary.
  private applyActiveProject(target: 'primary' | 'secondary', reason: string): void {
    const resolved: 'primary' | 'secondary' =
      target === 'secondary' && this.secondaryDb ? 'secondary' : 'primary';

    if (resolved === this.failoverState.currentProject) return;

    this.failoverState.currentProject = resolved;
    this.failoverState.isActive = resolved === 'secondary';
    this.failoverState.lastSwitchTime = new Date();
    this.failoverState.switchReason = reason;
    Logger.info(`[FailoverService] Active project set to '${resolved}' (${reason})`);
  }

  // Public API - Get current failover state
  public getFailoverState(): FailoverState {
    return { ...this.failoverState };
  }

  // Public API - Get current active database
  public getActiveDatabase(): Firestore | null {
    return this.failoverState.currentProject === 'primary' ? this.primaryDb : this.secondaryDb;
  }

  // Public API - Get current active auth
  public getActiveAuth(): Auth | null {
    return this.failoverState.currentProject === 'primary' ? this.primaryAuth : this.secondaryAuth;
  }

  // Public API - Get current active storage
  public getActiveStorage(): FirebaseStorage | null {
    return this.failoverState.currentProject === 'primary' ? this.primaryStorage : this.secondaryStorage;
  }

  // Public API - Check if failover is active
  public isFailoverActive(): boolean {
    return this.failoverState.isActive;
  }

  // Public API - Get current project
  public getCurrentProject(): 'primary' | 'secondary' {
    return this.failoverState.currentProject;
  }

  // Public API - Manual switch (admin/ops or tests). The canonical, fleet-wide
  // switch is updating the `system/failover` doc; this applies the change to the
  // local client only.
  public async manualFailover(targetProject: 'primary' | 'secondary', reason: string): Promise<void> {
    this.applyActiveProject(targetProject, reason || 'manual override');
  }

  // Cleanup method
  public destroy(): void {
    if (this.controlDocUnsubscribe) {
      this.controlDocUnsubscribe();
      this.controlDocUnsubscribe = null;
    }
  }
}

// Create singleton instance
const failoverService = new FailoverService();

// Export singleton and class for testing
export { failoverService as default, FailoverService };

// Export convenience functions
export const getFailoverState = () => failoverService.getFailoverState();
export const getActiveDatabase = () => failoverService.getActiveDatabase();
export const getActiveAuth = () => failoverService.getActiveAuth();
export const getActiveStorage = () => failoverService.getActiveStorage();
export const isFailoverActive = () => failoverService.isFailoverActive();
export const getCurrentProject = () => failoverService.getCurrentProject();
export const manualFailover = (targetProject: 'primary' | 'secondary', reason: string) =>
  failoverService.manualFailover(targetProject, reason);
